import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import type { DashboardFilters, FilterPreset } from '../types';

interface FilterToolbarProps {
  filters: DashboardFilters;
  onFiltersChange: (filters: DashboardFilters) => void;
  presets: FilterPreset[];
  onApplyPreset: (preset: FilterPreset) => void;
}

const FilterToolbar = ({ filters, onFiltersChange, presets, onApplyPreset }: FilterToolbarProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const statusOptions = [
    { value: 'success', label: 'Sucesso' },
    { value: 'error', label: 'Erro' },
    { value: 'pending', label: 'Pendente' },
    { value: 'warning', label: 'Aviso' }
  ];

  const presetOptions = presets.map(preset => ({
    value: preset.id,
    label: preset.name
  }));

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      searchTerm: e.target.value
    });
  };

  const handleStatusChange = (value: string | string[]) => {
    onFiltersChange({
      ...filters,
      status: Array.isArray(value) ? value : [value]
    });
  };

  const handlePresetChange = (value: string | string[]) => {
    const presetId = Array.isArray(value) ? value[0] : value;
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      onApplyPreset(preset);
    }
  };

  const handleClearFilters = () => {
    onFiltersChange({
      dateRange: { start: null, end: null },
      status: [],
      searchTerm: ''
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Buscar por empresa, CNPJ..."
            value={filters.searchTerm}
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>

        <div className="flex gap-2">
          <Select
            options={statusOptions}
            value={filters.status}
            onChange={handleStatusChange}
            placeholder="Status"
            multiple
            className="w-48"
          />

          <Select
            options={presetOptions}
            onChange={handlePresetChange}
            placeholder="Filtros Salvos"
            className="w-48"
          />

          <Button
            variant="outline"
            iconName={showAdvanced ? 'ChevronUp' : 'ChevronDown'}
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-label="Filtros avançados"
          />

          <Button
            variant="ghost"
            iconName="X"
            onClick={handleClearFilters}
            aria-label="Limpar filtros"
          />
        </div>
      </div>

      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Data Inicial"
              value={filters.dateRange.start?.toISOString().split('T')[0] || ''}
              onChange={(e) => onFiltersChange({
                ...filters,
                dateRange: {
                  ...filters.dateRange,
                  start: e.target.value ? new Date(e.target.value) : null
                }
              })}
            />
            <Input
              type="date"
              label="Data Final"
              value={filters.dateRange.end?.toISOString().split('T')[0] || ''}
              onChange={(e) => onFiltersChange({
                ...filters,
                dateRange: {
                  ...filters.dateRange,
                  end: e.target.value ? new Date(e.target.value) : null
                }
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterToolbar;